import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3';
import { format, startOfWeek, startOfMonth, parseISO, eachMonthOfInterval, eachWeekOfInterval, isSameMonth, isSameWeek, min, max } from 'date-fns';
import { de } from 'date-fns/locale';
import { X, Maximize2 } from 'lucide-react';
import CategoryIcon from '../common/CategoryIcon';
import Tooltip from "../common/Tooltip";
import SectionLabel from '../common/SectionLabel';
import Card from '../common/Card';

const SingleCategoryChart = ({ data, category, color, granularity, onHide, onExpand, isModal = false }) => {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const width = isModal ? 1000 : 600;
    const height = isModal ? 400 : 200;
    const margin = { top: 20, right: 30, bottom: 30, left: 50 };

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3.select(svgRef.current)
      .attr("width", "100%")
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Scales
    const x = d3.scaleTime()
      .domain(d3.extent(data, d => d.date))
      .range([0, width - margin.left - margin.right]);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.value) * 1.1])
      .range([height - margin.top - margin.bottom, 0]);

    // Area generator
    const area = d3.area()
      .x(d => x(d.date))
      .y0(height - margin.top - margin.bottom)
      .y1(d => y(d.value))
      .curve(d3.curveMonotoneX);

    const line = d3.line()
      .x(d => x(d.date))
      .y(d => y(d.value))
      .curve(d3.curveMonotoneX);

    // Add Gradient
    const gradientId = `gradient-${category.id}`;
    const defs = svg.append("defs");
    const gradient = defs.append("linearGradient")
      .attr("id", gradientId)
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");

    gradient.append("stop")
      .attr("offset", "0%")
      .attr("stop-color", color)
      .attr("stop-opacity", 0.4);

    gradient.append("stop")
      .attr("offset", "100%")
      .attr("stop-color", color)
      .attr("stop-opacity", 0);

    // Draw Area
    svg.append("path")
      .datum(data)
      .attr("fill", `url(#${gradientId})`)
      .attr("d", area);

    // Draw Line
    svg.append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", color)
      .attr("stroke-width", 2.5)
      .attr("d", line);

    // Axes
    const xAxis = d3.axisBottom(x)
      .ticks(granularity === 'month' ? d3.timeMonth.every(1) : d3.timeWeek.every(2))
      .tickFormat(d => {
        if (!isModal) {
          const ticks = x.ticks(granularity === 'month' ? d3.timeMonth.every(1) : d3.timeWeek.every(2));
          const domain = x.domain();

          const indices = [
            0,
            Math.floor(ticks.length * 0.25),
            Math.floor(ticks.length * 0.5),
            Math.floor(ticks.length * 0.75),
            ticks.length - 1
          ];

          const isTargetTick = indices.some(idx =>
            ticks[idx] && d.getTime() === ticks[idx].getTime()
          );

          if (isTargetTick || d.getTime() === domain[0].getTime() || d.getTime() === domain[1].getTime()) {
            return format(d, granularity === 'month' ? 'MMM yy' : 'dd.MM', { locale: de });
          }
          return "";
        }
        return format(d, granularity === 'month' ? 'MMM yy' : 'dd.MM', { locale: de });
      });

    const xAxisG = svg.append("g")
      .attr("transform", `translate(0,${height - margin.top - margin.bottom})`)
      .call(xAxis)
      .attr("color", "#cbd5e1");

    xAxisG.selectAll("text")
      .style("font-size", "14px")
      .style("fill", "#64748b");

    if (!isModal) {
      xAxisG.selectAll(".tick line").style("opacity", (d) => {
        const ticks = x.ticks(granularity === 'month' ? d3.timeMonth.every(1) : d3.timeWeek.every(2));
        const domain = x.domain();

        const indices = [
          0,
          Math.floor(ticks.length * 0.25),
          Math.floor(ticks.length * 0.5),
          Math.floor(ticks.length * 0.75),
          ticks.length - 1
        ];

        const isTargetTick = indices.some(idx =>
          ticks[idx] && d.getTime() === ticks[idx].getTime()
        );

        return (isTargetTick || d.getTime() === domain[0].getTime() || d.getTime() === domain[1].getTime()) ? 1 : 0;
      });
    }

    svg.append("g")
      .call(d3.axisLeft(y).ticks(5).tickFormat(d => `${d}€`))
      .attr("color", "#cbd5e1")
      .selectAll("text")
      .style("font-size", "14px")
      .style("fill", "#64748b");

    // Tooltip
    const tooltip = d3.select("body").append("div")
      .attr("class", "d3-tooltip")
      .style("position", "absolute")
      .style("visibility", "hidden")
      .style("background-color", "white")
      .style("border", "1px solid #e2e8f0")
      .style("border-radius", "0.75rem")
      .style("padding", "0.5rem 0.75rem")
      .style("box-shadow", "0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.02)")
      .style("font-size", "0.8rem")
      .style("font-weight", "600")
      .style("pointer-events", "none")
      .style("z-index", 1000);

    const bisect = d3.bisector(d => d.date).left;

    const focus = svg.append("g")
      .style("opacity", 0);

    focus.append("circle")
      .attr("r", 4.5)
      .attr("fill", color)
      .attr("stroke", "white")
      .attr("stroke-width", 2.5);

    svg.append("rect")
      .attr("width", width - margin.left - margin.right)
      .attr("height", height - margin.top - margin.bottom)
      .style("fill", "none")
      .style("pointer-events", "all")
      .style("cursor", "crosshair")
      .on("mouseover", () => {
        focus.style("opacity", 1);
        tooltip.style("visibility", "visible");
      })
      .on("mouseout", () => {
        focus.style("opacity", 0);
        tooltip.style("visibility", "hidden");
      })
      .on("mousemove", (event) => {
        const pointer = d3.pointer(event);
        const x0 = x.invert(pointer[0]);
        const i = bisect(data, x0, 1);
        const d0 = data[i - 1];
        const d1 = data[i];
        const d = (d1 && d0) ? (x0 - d0.date > d1.date - x0 ? d1 : d0) : (d0 || d1);

        if (d) {
          focus.attr("transform", `translate(${x(d.date)},${y(d.value)})`);
          tooltip
            .html(`
              <div class="text-neutral-900">${format(d.date, granularity === 'month' ? 'MMMM yyyy' : 'dd. MMMM yyyy', { locale: de })}</div>
              <div class="text-primary-600 font-mono mt-0.5">${d.value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</div>
            `)
            .style("top", (event.pageY - 10) + "px")
            .style("left", (event.pageX + 10) + "px");
        }
      });

    return () => {
      tooltip.remove();
    };
  }, [data, category, color, granularity, isModal]);

  return (
    <Card className={`p-4 ${isModal ? '' : 'shadow-sm'}`}>
      <div className="flex items-center justify-between mb-2">
        {!isModal && (
          <>
            <h4 className="text-sm font-bold text-neutral-700 flex items-center gap-2">
              <CategoryIcon name={category.icon_name} size={15} className="text-neutral-500" />
              <span>{category.name}</span>
            </h4>
            <div className="flex items-center gap-1">
              <button
                onClick={onExpand}
                className="p-1.5 text-neutral-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all"
                title="Vergrößern"
              >
                <Maximize2 size={14} />
              </button>
              <button
                onClick={onHide}
                className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                title="Ausblenden"
              >
                <X size={14} />
              </button>
            </div>
          </>
        )}
      </div>
      <div className={`relative overflow-visible ${isModal ? '' : 'cursor-pointer'}`} onClick={!isModal ? onExpand : undefined}>
        <svg ref={svgRef} className="overflow-visible w-full"></svg>
      </div>
    </Card>
  );
};

const ChartModal = ({ category, data, granularity, color, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[60]" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl overflow-hidden border border-neutral-100" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-100 flex justify-between items-center bg-neutral-50/50">
          <div className="flex items-center gap-2.5">
            <CategoryIcon name={category.icon_name} size={17} className="text-neutral-500" />
            <h3 className="text-sm font-bold text-neutral-800">{category.name}</h3>
            <span className="text-[10px] font-bold text-neutral-400 bg-white px-2 py-0.5 rounded-full border border-neutral-200 uppercase tracking-wider">
              {granularity === 'month' ? 'Monatlich' : 'Wöchentlich'}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-xl transition-all">
            <X size={20} />
          </button>
        </div>
        <div className="p-6">
          <SingleCategoryChart
            category={category}
            data={data}
            granularity={granularity}
            color={color}
            isModal={true}
          />
        </div>
      </div>
    </div>
  );
};

const CategoryTimeCharts = ({ transactions, categories, granularity }) => {
  const processedData = useMemo(() => {
    if (!transactions || transactions.length === 0) return {};

    const now = new Date();
    const startOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const relevant = transactions.filter(t =>
      t.date && parseISO(t.date) < startOfNextMonth
    );
    if (relevant.length === 0) return {};

    const dates = relevant.map(t => parseISO(t.date));
    const minDate = min(dates);
    const maxDate = max(dates);

    let intervals;
    if (granularity === 'month') {
      intervals = eachMonthOfInterval({ start: startOfMonth(minDate), end: startOfMonth(maxDate) });
    } else {
      intervals = eachWeekOfInterval({ start: startOfWeek(minDate), end: startOfWeek(maxDate) });
    }

    const buckets = intervals.map(() => new Map());
    relevant.forEach(t => {
      const tDate = parseISO(t.date);
      const intervalIndex = intervals.findIndex(date =>
        granularity === 'month' ? isSameMonth(tDate, date) : isSameWeek(tDate, date, { locale: de })
      );
      if (intervalIndex === -1) return;

      const bucket = buckets[intervalIndex];
      t.categories.forEach(cat => {
        const catAmt = t.categories.length > 1 && cat.split_amount
          ? Math.abs(cat.split_amount)
          : Math.abs(t.amount) / (t.categories.length || 1);
        const signedAmt = t.amount < 0 ? -catAmt : catAmt;
        bucket.set(cat.id, (bucket.get(cat.id) || 0) + signedAmt);
      });
    });

    const dataByCategory = {};

    categories.filter(cat => Boolean(cat.include_in_analytics)).forEach(cat => {
      const timeSeries = intervals.map((date, i) => {
        const net = buckets[i].get(cat.id) || 0;
        return { date, value: Math.max(0, -net) };
      });

      const totalSum = timeSeries.reduce((acc, d) => acc + d.value, 0);
      if (totalSum > 0) {
        dataByCategory[cat.id] = timeSeries;
      }
    });

    return dataByCategory;
  }, [transactions, categories, granularity]);

  const [visibleCategoryIds, setVisibleCategoryIds] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const hasDefaultedVisible = useRef(false);

  useEffect(() => {
    if (hasDefaultedVisible.current) return;
    const ids = Object.keys(processedData);
    if (ids.length === 0) return;

    hasDefaultedVisible.current = true;
    const topIds = ids
      .map(id => ({ id, total: processedData[id].reduce((sum, d) => sum + d.value, 0) }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 3)
      .map(e => Number(e.id));
    setVisibleCategoryIds(topIds);
  }, [processedData]);

  const toggleCategory = (id) => {
    setVisibleCategoryIds(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  if (Object.keys(processedData).length === 0) {
    return <div className="p-8 text-center text-neutral-400 text-sm">Keine Verlaufsdaten für diesen Zeitraum vorhanden.</div>;
  }

  return (
    <>
      <Card className="p-4 mb-4">
        <SectionLabel as="span">Diagramme anzeigen</SectionLabel>

        <div className="flex flex-wrap gap-1.5 pt-3">
          {categories.filter(c => processedData[c.id]).map(cat => (
            <button
              key={cat.id}
              onClick={() => toggleCategory(cat.id)}
              className={`px-2 py-1 rounded-lg text-xs font-semibold border transition-all flex items-center gap-1.5 ${
                visibleCategoryIds.includes(cat.id)
                  ? 'bg-primary-50 border-primary-200 text-primary-700'
                  : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <Tooltip content={cat.name}>
                <CategoryIcon
                    name={cat.icon_name}
                    size={20}
                    className={visibleCategoryIds.includes(cat.id) ? 'text-primary-600' : 'text-neutral-500'}
                />
              </Tooltip>
            </button>
          ))}
        </div>
      </Card>

      <div className="space-y-4">
        {visibleCategoryIds.map(id => {
          const category = categories.find(c => c.id === parseInt(id));
          if (!category) return null;
          const color = category.color || d3.schemePaired[categories.indexOf(category) % 12];
          return (
            <SingleCategoryChart
              key={id}
              category={category}
              data={processedData[id]}
              granularity={granularity}
              color={color}
              onHide={() => toggleCategory(id)}
              onExpand={() => setExpandedCategory({ category, data: processedData[id], color })}
            />
          );
        })}
      </div>

      {expandedCategory && (
        <ChartModal
          category={expandedCategory.category}
          data={expandedCategory.data}
          granularity={granularity}
          color={expandedCategory.color}
          onClose={() => setExpandedCategory(null)}
        />
      )}
    </>
  );
};

export default CategoryTimeCharts;
